import { KeyValue } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Signal } from '@angular/core';
import { INavData } from '@coreui/angular';
import { gunzipSync, zipSync } from 'fflate';
import { ToastrService } from 'ngx-toastr';
import { from, Observable } from 'rxjs';
import {
  AGKeyword,
  allNavList,
  BDSecurityKeyword,
  BerthelKeyword,
  EnterpriseKeyword,
  IFGKeyword,
  LPLKeyword,
  NavList,
  ProEquitiesKeyword,
  SecureKeyword,
  UnifyKeyword
} from '../../layout/layout/app-main.modal';
import { BFFBaseFilter } from '../api';
import {
  COMPLIANCE,
  DASHBOARD,
  DeviceManagementType,
  LOGIN_SOURCE,
  LOGIN_SOURCE_KEY,
  PageTitles,
  PLATFORM_VALUES,
  POSITION_IDS,
  POSITION_IDS_LIST,
  Roles,
  SORT_ORDER,
  USER_STATUS
} from '../constants';
import { OS, REQ, ReqState, UserDetail } from '../models';
import { ColDef, ColumnState, GridApi } from 'ag-grid-enterprise';

export const translatePlatformToKeyValue = (
  values: typeof PLATFORM_VALUES
): KeyValue<string, string>[] => {
  const translatedValue = Object.keys(values).map((key: string) => {
    return PLATFORM_VALUES[key];
  });
  return translatedValue;
};

export const translateDeviceManagementTypeToKeyValue = (
  values: typeof DeviceManagementType
): KeyValue<string, string>[] => {
  const translatedValue = Object.keys(values).map((key: string) => {
    return DeviceManagementType[key];
  });
  return translatedValue;
};

export const translateCyberscoreFiltersToKeyValue = (
  values: string[]
): KeyValue<string, string>[] => {
  return values.map((value: string) => {
    return {
      key: value,
      value: value
    };
  });
};

export const setBusinessUnitIdsAsFilters = (
  applianceIdList: string[]
): BFFBaseFilter[] => {
  return [
    {
      searchType: 'equals',
      values: applianceIdList,
      column: 'businessUnitId'
    }
  ];
};

export const translateUserStatusToKeyValue = (
  values: typeof USER_STATUS
): string[] => {
  const translatedValue = Object.keys(values).map((key: string) => {
    return {
      key: key,
      value: USER_STATUS[key]
    };
  });
  return translatedValue.map((val) => val.value);
};

export const getOSData = (value): OS => {
  let info = {
    title: 'Error',
    icon: 'cil-crop'
  };
  switch (value?.trim()) {
    case PLATFORM_VALUES.WIN:
      info = { title: PLATFORM_VALUES.WIN, icon: 'cib-windows' };
      break;
    case PLATFORM_VALUES.Mac:
      info = { title: PLATFORM_VALUES.Mac, icon: 'cib-apple' };
      break;
    case PLATFORM_VALUES.iOS:
      info = { title: PLATFORM_VALUES.iOS, icon: 'cib-app-store-ios' };
      break;
    case PLATFORM_VALUES.Android:
      info = { title: PLATFORM_VALUES.Android, icon: 'cib-android' };
      break;
    case PLATFORM_VALUES.Chrome_OS:
      info = { title: PLATFORM_VALUES.Chrome_OS, icon: 'cib-google-chrome' };
      break;
    case PLATFORM_VALUES.Others:
      info = { title: PLATFORM_VALUES.Others, icon: 'cil-devices' };
      break;
    default:
      info = { title: '', icon: '' };
  }
  return info;
};

export const showToasterMessage = (
  reqState: ReqState,
  toastrService: ToastrService
) => {
  const message = REQ.getMessage(reqState, reqState?.message);
  REQ.isSuccess(reqState)
    ? toastrService.success(message)
    : toastrService.error(message);
};

export const getInitials = (firstName: string, lastName: string) =>
  `${firstName?.substring(0, 1)?.toUpperCase()}${lastName?.substring(0, 1)?.toUpperCase()}`;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getErrorDetails = (response: HttpErrorResponse | any) => {
  const isHttpError = response instanceof HttpErrorResponse ? true : false;
  const errorResponse = isHttpError ? (response as HttpErrorResponse) : null;
  const errorData = isHttpError ? errorResponse?.error : null;

  return { isHttpError, errorResponse, errorData };
};

// Converts object values to an array
export const extractValuesFromObject = (values: {
  [key: string]: string;
}): string[] => {
  return Object.values(values);
};

//SO Position User check
export const isSOUser = (userDetails$: Signal<UserDetail>): boolean =>
  userDetails$().position_id === POSITION_IDS.SO;

//SA Position User check
export const isSAUser = (userDetails$: Signal<UserDetail>): boolean =>
  userDetails$().position_id === POSITION_IDS.SA;

export const getRoleLabel = (role: Roles) => {
  let roleLabel = '';
  if (role) {
    const positionList = POSITION_IDS_LIST[role];
    roleLabel = `POS ${positionList.positionId} - ${role}`;
  }
  return roleLabel;
};

// Extract CSV file from GZip
export function extractCSVFileFromGZip(zippedFile: Blob): Observable<Blob> {
  return from(
    zippedFile.arrayBuffer().then((buffer) => {
      const decompressedData = gunzipSync(new Uint8Array(buffer));
      const decodedString = new TextDecoder().decode(decompressedData);
      return new Blob([decodedString], { type: 'text/csv' });
    })
  );
}

// Convert to Zip
export const zipFiles = (
  files: { name: string; file: Blob }[]
): Observable<Blob> =>
  from(
    Promise.all(
      files.map(async ({ name, file }) => [
        name,
        new Uint8Array(await file.arrayBuffer())
      ])
    ).then(
      (entries) =>
        new Blob([zipSync(Object.fromEntries(entries))], {
          type: 'application/zip'
        })
    )
  );

// TODO: temp function, need to be removed after Login screens implementation
export const getBasePortal = (portalName: string): string => {
  if (LPLKeyword.includes(portalName)) {
    return 'lpl';
  } else if (SecureKeyword.includes(portalName)) {
    return 'secure';
  } else if (AGKeyword.includes(portalName)) {
    return 'ag';
  } else if (BDSecurityKeyword.includes(portalName)) {
    return 'bdsecurity';
  } else if (IFGKeyword.includes(portalName)) {
    return 'ifg';
  } else if (BerthelKeyword.includes(portalName)) {
    return 'berthel';
  } else if (ProEquitiesKeyword.includes(portalName)) {
    return 'proequities';
  } else if (EnterpriseKeyword.includes(portalName)) {
    return 'enterprise';
  } else if (UnifyKeyword.includes(portalName)) {
    return 'unify';
  }
};

export const getCurrentRoute = () => {
  const hash = window.location.hash;
  const url = new URL(hash.slice(1), window.location.origin);
  return url.pathname;
};

//Accomodate getqueryparams in getCurrentRoute later
export const getQueryParams = (paramsToRemove?: string[]) => {
  const hash = window.location.hash;
  const urlObject = new URL(hash.slice(1), window.location.origin);
  const queryParams: Record<string, string> = {};
  urlObject.searchParams.forEach((value, key) => {
    queryParams[key] = value;
  });

  if (paramsToRemove?.length) {
    paramsToRemove.forEach((param) => {
      delete queryParams[param];
    });
  }
  return queryParams;
};

export const getTargetRoute = (
  portalName: string,
  positionId: POSITION_IDS,
  user: UserDetail,
  navigateToDefault: boolean = false
) => {
  const filteredMenu = getFilteredMenuList(user, portalName);
  //TODO: To be removed getFilteredMenuList-  temporary function to check permissions based on positionId and portalAccess
  const isDashboardEnabled = filteredMenu.some(
    (nav) => nav.name === PageTitles.DASHBOARD
  );
  const currentRoute = getCurrentRoute();

  const isPOS2Or1 =
    positionId === POSITION_IDS.DIVISION_MANAGER ||
    positionId === POSITION_IDS.TEAM_MANAGER;

  const defaultRoute = isPOS2Or1
    ? `/${COMPLIANCE}`
    : isDashboardEnabled
      ? `/${DASHBOARD}`
      : `/${COMPLIANCE}`;

  const isCurrentRouteAllowed = filteredMenu.some((nav) =>
    currentRoute.includes(nav?.url?.toString())
  );

  const isAppBootstrapRoute = ['/', '/auth/login'].includes(currentRoute);
  if (isAppBootstrapRoute || navigateToDefault) return defaultRoute;
  else return isCurrentRouteAllowed ? currentRoute : defaultRoute;
};

export const getFilteredMenuList = (user, portalName) => {
  //TODO: To be removed -  temporary function to check permissions based on positionId and portalAccess
  const filteredNavList = allNavList.filter((nav) => {
    return checkAdditionalPermissions(nav, user, portalName);
  });

  const filteredNavListBasedOnPermission = filteredNavList.filter((nav) => {
    if (!nav.url || !nav.permissions) {
      return true;
    }
    return checkPermission(nav.permissions, user.permissions);
  });
  // temporary function to check permissions based on positionId and portalAccess end

  const filteredMenuList: INavData[] = filteredNavListBasedOnPermission;
  return filteredMenuList;
};

export const isCutOverClient = (portalName: string) => {
  return ![...AGKeyword, ...SecureKeyword, ...LPLKeyword].includes(portalName);
};

//TODO: temporary function to check permissions based on positionId and portalAccess
export const checkAdditionalPermissions = (
  nav: NavList,
  user: UserDetail,
  portalName: string
): boolean => {
  let accessAllowed = false;
  if (nav.positionId && nav.portalAccess) {
    accessAllowed =
      nav.positionId.includes(user.position_id) &&
      nav.portalAccess.includes(portalName);
    //optional bu check check
    //buCheck will be present for few nav items which needs to be checked for BU access in addition to positionId and portalAccess.
    if (nav.buCheck?.length && accessAllowed) {
      const buCheck = nav.buCheck.find((check) =>
        check.portalName.includes(portalName)
      );
      if (buCheck && !buCheck.buIds.includes(user.businessUnitId)) {
        accessAllowed = false;
      }
    }
    //optional role check
    // role check will be present for few nav items which needs to be checked for role access in addition to positionId and portalAccess.
    if (nav?.roles?.length && accessAllowed) {
      accessAllowed = accessAllowed && nav.roles.includes(user.role as Roles);
    }
  } else {
    accessAllowed = true; // Default to true if no permissions or positionId are defined
  }
  return accessAllowed;
};

export const checkPermission = (
  navListPermissions,
  userPermissions
): boolean => {
  const userPermissionsSet = new Set(userPermissions);
  return navListPermissions?.some((item): boolean =>
    userPermissionsSet.has(item)
  );
};

export const buildTokenKey = (portalName: string): string => {
  return `${portalName}_refreshToken`; // Use portalName to generate a unique key
};

//TODO check usage of below function
// const getPortalFromUrl = (): string => {
//   const url: URL = new URL(location.href.replace('/#', ''));
//   //example of URL - https://lpl.cyber.smarsh.com
//   let portalName: string = url.hostname.split('.')[0] ?? '';

//   //For local development use portalName from query param
//   portalName = url.searchParams.get('portalName') ?? portalName;
//   return portalName;
// };

export const isAuthenticated = (
  portalName: string,
  skipInInitMode = false // allow to skip token check in inti mode - required for drupal case
): boolean => {
  if (skipInInitMode) return true;
  let token = null;
  if (portalName) {
    const tokenKey = buildTokenKey(portalName);
    token = localStorage.getItem(tokenKey); // Fetch the token from the dynamically generated key
  }
  return token ? true : false;
};

export const getLoginSource = (portal): LOGIN_SOURCE =>
  localStorage.getItem(generateLoginSourceKey(portal)) as LOGIN_SOURCE;

export const setLoginSource = (source: LOGIN_SOURCE, portal) =>
  localStorage.setItem(generateLoginSourceKey(portal), source);

export const isNgpLogin = (source: LOGIN_SOURCE) => {
  return source === LOGIN_SOURCE.NGP;
};

export const isDrupalLogin = (source: LOGIN_SOURCE) => {
  return source === LOGIN_SOURCE.DRUPAL;
};

export const isCognitoLogin = (source: LOGIN_SOURCE) => {
  return source === LOGIN_SOURCE.COGNITO;
};

export const generateLoginSourceKey = (portal) =>
  `${portal}_${LOGIN_SOURCE_KEY}`;

export const capitalizeFirstLetter = (str: string) => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const allowOnlyNumbers = (event: KeyboardEvent) => {
  const allowedKeys = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
  if (!allowedKeys.includes(event.key)) {
    event.preventDefault();
  }
};

export const sortArrayByAlphabetically = (data, sortKey, sortType) => {
  return sortType === SORT_ORDER.ASC
    ? data.sort((a, b) => a[sortKey].localeCompare(b[sortKey]))
    : data.sort((a, b) => b[sortKey].localeCompare(a[sortKey]));
};

export const enableSendEmail = (id: string) => {
  const NLApplianceIds = [
    '16d86f98-4c4e-5862-95ef-1eca59c8e309',
    '162576b0-d86e-5f2d-bf0b-c6ebd725d565'
  ]; //Appliance Ids of NL [stageId,prodId]
  return NLApplianceIds.includes(id);
};

export const getFieldsByKey = (data: ColumnState[], key: string) => {
  return data.map((item) => item[key]);
};

export const isGridColumnsChanged = (defaultColDef, gridAPI) => {
  const currentGridFields = getFieldsByKey(gridAPI.getColumnState(), 'colId');
  const defaultGridFields = getFieldsByKey(defaultColDef, 'field');
  const sorted1 = [...currentGridFields].sort();
  const sorted2 = [...defaultGridFields].sort();
  return !sorted1.every((item, index) => item === sorted2[index]);
};

export const preserveColumnsVisbility = (
  defaultColDef: ColDef[],
  gridApi: GridApi,
  columnsStateToPersist: string[]
) => {
  const displayedColumns = gridApi.getColumnState();
  return displayedColumns.length
    ? defaultColDef.map((col) => {
        if (columnsStateToPersist.includes(col.field)) {
          col.hide = displayedColumns.find(
            (displayed_col) => displayed_col.colId === col.field
          )?.hide;
        }
        return col;
      })
    : defaultColDef;
};

export const isEmptyFilterEqualOption = (filterData) => {
  return filterData.find(
    (filter) => filter.searchType === 'equals' && !filter.values.length
  );
};
